class CurrencyLocalizationForm extends HTMLElement {
  static PANEL_TRANSITION_MS = 300;

  constructor() {
    super();
    this._initialized = false;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeyUp = this.onDocumentKeyUp.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
  }

  connectedCallback() {
    if (this._initialized) {
      return;
    }

    this.picker = this.querySelector('.currency-picker');
    this.form = this.querySelector('form');
    this.countryInput = this.querySelector('.currency-picker__country-input');
    this.trigger = this.querySelector('.currency-picker__trigger');
    this.panel = this.querySelector('.currency-picker__panel');
    this.options = this.querySelectorAll('.currency-picker__option');
    this.triggerLabel = this.querySelector('.currency-picker__trigger-label');

    if (!this.picker || !this.form || !this.countryInput || !this.trigger || !this.panel) {
      return;
    }

    this.countryInput.disabled = false;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeyDown);

    this.options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick);
    });

    this._initialized = true;
  }

  disconnectedCallback() {
    if (!this._initialized) {
      return;
    }

    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.trigger.removeEventListener('keydown', this.onTriggerKeyDown);

    this.options.forEach((option) => {
      option.removeEventListener('click', this.onOptionClick);
    });

    this.closePanel();
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }
    this._initialized = false;
  }

  onTriggerClick() {
    this.togglePanel();
  }

  onTriggerKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePanel();
    }
  }

  onOptionClick(event) {
    this.onOptionSelect(event.currentTarget);
  }

  get storeCountry() {
    return this.picker.dataset.storeCountry;
  }

  /*
   * Destination getters are store-neutral. The Liquid layer decides WHICH
   * storefront is the external one (US store -> ANZ, ANZ store -> US) and
   * exposes it as data-external-*. The legacy data-us-* attributes are still
   * read as a fallback so this asset stays compatible with the ANZ branch.
   */
  get externalStoreUrl() {
    const url = this.picker.dataset.externalStoreUrl || this.picker.dataset.usStoreUrl || '';
    return url.replace(/\/$/, '');
  }

  get productHandle() {
    return this.picker.dataset.productHandle;
  }

  get externalRedirectPath() {
    return this.picker.dataset.externalRedirectPath || this.picker.dataset.usRedirectPath || '/';
  }

  get shouldVerifyProduct() {
    return (
      this.picker.dataset.externalVerifyProduct === 'true' ||
      this.picker.dataset.usVerifyProduct === 'true'
    );
  }

  isOpen() {
    return this.picker.classList.contains('currency-picker--open');
  }

  openPanel() {
    document.querySelectorAll('currency-localization-form').forEach((instance) => {
      if (instance !== this) {
        instance.closePanel();
      }
    });

    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }

    this.panel.hidden = false;
    requestAnimationFrame(() => {
      this.picker.classList.add('currency-picker--open');
    });
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keyup', this.onDocumentKeyUp);
  }

  closePanel({ returnFocus = false } = {}) {
    if (!this.picker || !this.panel || !this.trigger) {
      return;
    }

    this.picker.classList.remove('currency-picker--open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onDocumentKeyUp);

    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
    }

    this._closeTimeout = window.setTimeout(() => {
      if (!this.isOpen()) {
        this.panel.hidden = true;
      }

      this._closeTimeout = null;
    }, CurrencyLocalizationForm.PANEL_TRANSITION_MS);

    if (returnFocus) {
      this.trigger.focus();
    }
  }

  togglePanel() {
    if (this.isOpen()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  }

  onDocumentKeyUp(event) {
    if (event.key === 'Escape' && this.isOpen()) {
      event.stopPropagation();
      this.closePanel({ returnFocus: true });
    }
  }

  updateSelectedState(option) {
    this.options.forEach((item) => {
      const isSelected = item === option;
      const isLocalization = item.dataset.action === 'localization';

      item.classList.toggle('currency-picker__option--selected', isSelected);

      const indicator = item.querySelector('.currency-picker__indicator');
      if (indicator) {
        indicator.classList.toggle('currency-picker__indicator--selected', isSelected);
      }

      if (isLocalization && isSelected) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (this.triggerLabel && option.dataset.currency) {
      this.triggerLabel.textContent = option.dataset.currency;
    }
  }

  buildExternalDestinationUrl(path, countryHint) {
    const blockedParams = new Set([
      'preview_theme_id',
      '_ab',
      'key',
      'oseid',
      'pb',
      'country',
      'currency',
    ]);

    const storeBase = `${this.externalStoreUrl}/`;
    const storeOrigin = new URL(storeBase).origin;
    let url;

    try {
      const isAbsoluteUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path);

      if (isAbsoluteUrl) {
        url = new URL(path);

        if (url.origin !== storeOrigin) {
          url = new URL('/', storeBase);
        }
      } else {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        url = new URL(normalizedPath, storeBase);
      }
    } catch {
      url = new URL('/', storeBase);
    }

    blockedParams.forEach((key) => {
      url.searchParams.delete(key);
    });

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (blockedParams.has(key) || url.searchParams.has(key)) {
        return;
      }

      url.searchParams.set(key, value);
    });

    // Applied last, on purpose: 'country' is a blocked param above so the
    // source page's country never leaks, but an explicitly chosen destination
    // market must still be pinned (the ANZ store serves NZ and AU from one
    // storefront and would otherwise fall back to its default currency).
    if (countryHint) {
      url.searchParams.set('country', countryHint);
    }

    return url.toString();
  }

  redirectToExternalProduct(productHandle, countryHint) {
    const productUrl = this.buildExternalDestinationUrl(`/products/${productHandle}`, countryHint);
    const jsonUrl = `${this.externalStoreUrl}/products/${productHandle}.json`;

    fetch(jsonUrl)
      .then((res) => {
        if (res.status === 404) {
          window.location.href = this.buildExternalDestinationUrl('/', countryHint);
          return;
        }

        window.location.href = productUrl;
      })
      .catch(() => {
        window.location.href = productUrl;
      });
  }

  redirectToExternalStore(countryHint) {
    // Without a configured destination there is nowhere to send the customer.
    // Bail out quietly rather than throwing from the URL constructor.
    if (!this.externalStoreUrl) {
      return;
    }

    const path = this.externalRedirectPath;

    if (!path || path === '/') {
      window.location.href = this.buildExternalDestinationUrl('/', countryHint);
      return;
    }

    if (this.shouldVerifyProduct && this.productHandle) {
      this.redirectToExternalProduct(this.productHandle, countryHint);
      return;
    }

    window.location.href = this.buildExternalDestinationUrl(path, countryHint);
  }

  evaluateRouting({ countryCode, action, isUserAction, externalCountry }) {
    if (action === 'redirect-external' || action === 'redirect-us') {
      this.redirectToExternalStore(externalCountry);
      return;
    }

    if (isUserAction && countryCode) {
      this.countryInput.value = countryCode;
      this.form.submit();
    }
  }

  onOptionSelect(option) {
    const countryCode = option.dataset.countryCode;
    const action = option.dataset.action;

    if (action !== 'redirect-external' && action !== 'redirect-us') {
      this.updateSelectedState(option);
    }

    this.closePanel();

    this.evaluateRouting({
      countryCode,
      action,
      externalCountry: option.dataset.externalCountry,
      isUserAction: true,
    });
  }
}

if (!customElements.get('currency-localization-form')) {
  customElements.define('currency-localization-form', CurrencyLocalizationForm);
}
